import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { EmptyStateComponent, LoadingSkeletonComponent } from '@/shared/ui';
import { BacnetDiscoveredDevice, BacnetObjectInfo, BacnetStatus } from 'src/api/bacnet';
import { CloudBacnetService } from '@/cloud/services/cloud-bacnet.service';
import { DeviceContextService } from '../device-context.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

/**
 * Read-only BACnet status for a device in the cloud portal.
 *
 * Mirrors what the on-device dashboard shows, without the controls: starting a discovery puts
 * broadcast traffic on the customer's building network and installing a licence forces a reboot,
 * so both stay with whoever is on site.
 */
@Component({
    selector: 'app-cloud-bacnet',
    standalone: true,
    imports: [CommonModule, ButtonModule, SectionCardComponent, TableModule, TagModule, TooltipModule, MessageModule, EmptyStateComponent, LoadingSkeletonComponent],
    templateUrl: './cloud-bacnet.component.html',
    styleUrl: './cloud-bacnet.component.scss',
    providers: [CloudBacnetService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloudBacnetComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private deviceCtx = inject(DeviceContextService);
    private bacnet = inject(CloudBacnetService);

    status = signal<BacnetStatus | null>(null);
    objects = signal<BacnetObjectInfo[]>([]);
    discoveredDevices = signal<BacnetDiscoveredDevice[]>([]);
    discoveryStatus = signal<string | null>(null);
    loading = signal(false);
    error = signal<string | null>(null);

    endpointsLabel = computed(() => {
        const status = this.status();
        return status ? `${status.endpointsUsed} of ${status.endpointsLimit}` : '—';
    });

    ngOnInit(): void {
        this.load();
    }

    async load(): Promise<void> {
        const deviceId = this.route.snapshot.paramMap.get('deviceId') ?? this.route.parent?.snapshot.paramMap.get('deviceId') ?? this.deviceCtx.device()?.id ?? '';

        if (!deviceId) {
            this.error.set('No device selected.');
            return;
        }

        this.loading.set(true);
        this.error.set(null);
        try {
            const response = await this.bacnet.fetch(deviceId);
            this.status.set(response?.status?.status ?? null);
            this.objects.set(response?.status?.objects ?? []);
            this.discoveredDevices.set(response?.discovery?.devices ?? []);
            this.discoveryStatus.set(response?.discovery?.state?.status ?? null);
        } catch (e: unknown) {
            // The device must be connected through the tunnel for this to work at all.
            this.error.set(this.describe(e));
        } finally {
            this.loading.set(false);
        }
    }

    private describe(e: unknown): string {
        if (typeof e === 'object' && e !== null && 'error' in e) {
            const body = (e as { error?: { error?: string } }).error;
            if (body?.error) return body.error;
        }
        return e instanceof Error ? e.message : 'Failed to read BACnet status from the device';
    }
}
